import { 
  collection, 
  doc, 
  getDocs, 
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * 컬렉션 이름 마이그레이션 서비스
 * assignments → generalAssignments로 변경
 */
export class CollectionMigrationService {
  private result = {
    success: false,
    processedCount: 0,
    errorCount: 0,
    errors: [] as string[],
    duration: 0
  };

  /**
   * 컬렉션 이름 마이그레이션 실행
   */
  async runCollectionMigration(userId: string, classId: string): Promise<void> {
    const startTime = Date.now();
    
    try {
      console.log('🚀 컬렉션 이름 마이그레이션 시작...');
      console.log('assignments → generalAssignments');
      
      // 1. assignments 컬렉션의 모든 문서 조회
      const oldAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'assignments');
      const oldAssignmentsSnap = await getDocs(oldAssignmentsRef);
      
      if (oldAssignmentsSnap.empty) {
        console.log('📝 마이그레이션할 assignments 문서가 없습니다.');
        this.result.success = true;
        this.result.duration = Date.now() - startTime;
        return;
      }
      
      const batch = writeBatch(db);
      
      // 2. 각 문서를 generalAssignments로 복사
      for (const assignmentDoc of oldAssignmentsSnap.docs) {
        const assignmentData = assignmentDoc.data();
        const newAssignmentRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignments', assignmentDoc.id);
        
        batch.set(newAssignmentRef, assignmentData);
        this.result.processedCount++;
        
        console.log(`✅ 일반 과제 복사: ${assignmentDoc.id}`);
      }
      
      // 3. assignmentStatuses도 마이그레이션
      await this.migrateAssignmentStatuses(userId, classId, batch);
      
      // 4. 새 컬렉션에 문서 저장
      await batch.commit();
      
      // 5. 기존 컬렉션 삭제 (별도 배치로 처리)
      await this.deleteOldCollections(userId, classId, oldAssignmentsSnap);
      
      this.result.success = true;
      this.result.duration = Date.now() - startTime;
      
      console.log('✅ 컬렉션 이름 마이그레이션 완료:', this.result);
    } catch (error) {
      console.error('❌ 컬렉션 이름 마이그레이션 실패:', error);
      this.result.errors.push(String(error));
      this.result.duration = Date.now() - startTime;
    }
  }

  /**
   * assignmentStatuses 마이그레이션
   */
  private async migrateAssignmentStatuses(userId: string, classId: string, batch: any): Promise<void> {
    console.log('📝 assignmentStatuses 마이그레이션 시작...');
    
    const oldStatusesRef = collection(db, 'users', userId, 'classes', classId, 'assignmentStatuses');
    const oldStatusesSnap = await getDocs(oldStatusesRef);
    
    for (const statusDoc of oldStatusesSnap.docs) {
      const assignmentId = statusDoc.id;
      
      // 각 과제의 멤버 상태들을 새 컬렉션으로 복사
      const studentsRef = collection(db, 'users', userId, 'classes', classId, 'assignmentStatuses', assignmentId, 'students');
      const studentsSnap = await getDocs(studentsRef);
      
      for (const studentDoc of studentsSnap.docs) {
        const studentData = studentDoc.data();
        const newStudentRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignmentStatuses', assignmentId, 'students', studentDoc.id);
        batch.set(newStudentRef, studentData);
      }
      
      console.log(`✅ assignmentStatuses 복사: ${assignmentId} (${studentsSnap.docs.length}명 멤버)`);
    }
  }

  /**
   * 기존 컬렉션들 삭제
   */
  private async deleteOldCollections(userId: string, classId: string, oldAssignmentsSnap: any): Promise<void> {
    console.log('🗑️ 기존 컬렉션 삭제 시작...');
    
    const batch = writeBatch(db);
    
    // 1. assignments 컬렉션 삭제
    for (const assignmentDoc of oldAssignmentsSnap.docs) {
      batch.delete(assignmentDoc.ref);
    }
    
    // 2. assignmentStatuses 컬렉션 삭제
    const oldStatusesRef = collection(db, 'users', userId, 'classes', classId, 'assignmentStatuses');
    const oldStatusesSnap = await getDocs(oldStatusesRef);
    
    for (const statusDoc of oldStatusesSnap.docs) {
      // 각 과제의 멤버 상태들도 삭제
      const studentsRef = collection(db, 'users', userId, 'classes', classId, 'assignmentStatuses', statusDoc.id, 'students');
      const studentsSnap = await getDocs(studentsRef);
      
      for (const studentDoc of studentsSnap.docs) {
        batch.delete(studentDoc.ref);
      }
      
      batch.delete(statusDoc.ref);
    }
    
    await batch.commit();
    console.log('✅ 기존 컬렉션 삭제 완료');
  }

  /**
   * 마이그레이션 결과 반환
   */
  getResult() {
    return this.result;
  }
}

export const collectionMigrationService = new CollectionMigrationService();
